import React from 'react';
import { formatCurrencyES, formatPeriodES } from '../../../config/reportTranslations';
import './Cuadro06.css';

/**
 * Cuadro 06: Evolución Balance USD millones
 *
 * Displays historical balance sheet evolution matching PDF report format:
 * - Period column (9 periods: dic-20 through current)
 * - Activo (Total Assets in USD millions)
 * - Pasivos (Total Liabilities in USD millions)
 * - Patrimonio (Total Equity in USD millions)
 *
 * Source: COAC financial statements / Elaboración: UnionRatings
 */

export interface Cuadro06Data {
  period: string;           // YYYY-MM format
  activo: number;          // Total assets in USD
  pasivo: number;          // Total liabilities in USD
  patrimonio: number;      // Total equity in USD
}

export interface Cuadro06Props {
  data: Cuadro06Data[];
  title?: string;
}

export const Cuadro06: React.FC<Cuadro06Props> = ({
  data,
  title = 'Cuadro 06: Evolución Balance USD millones'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="cuadro-06-container">
        <h4 className="cuadro-06-title">{title}</h4>
        <p className="cuadro-06-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Sort data by period (oldest first to match PDF format)
  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Convert to millions
  const toMillions = (value: number): number => value / 1_000_000;

  return (
    <div className="cuadro-06-container">
      <h4 className="cuadro-06-title">{title}</h4>

      <div className="cuadro-06-table-wrapper">
        <table className="cuadro-06-table">
          <thead>
            <tr>
              <th className="cuadro-06-header-period">Período</th>
              <th className="cuadro-06-header-numeric">Activo</th>
              <th className="cuadro-06-header-numeric">Pasivos</th>
              <th className="cuadro-06-header-numeric">Patrimonio</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr
                key={row.period}
                className={index === sortedData.length - 1 ? 'cuadro-06-row-current' : ''}
              >
                <td className="cuadro-06-cell-period">{formatPeriodES(row.period)}</td>
                <td className="cuadro-06-cell-numeric">{formatCurrencyES(toMillions(row.activo))}</td>
                <td className="cuadro-06-cell-numeric">{formatCurrencyES(toMillions(row.pasivo))}</td>
                <td className="cuadro-06-cell-numeric">{formatCurrencyES(toMillions(row.patrimonio))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Source note matching PDF format */}
      <p className="cuadro-06-source">
        Fuente: COAC / Elaboración: UnionRatings
      </p>
    </div>
  );
};

export default Cuadro06;
